import type {
  FormSchema,
  FormPage,
  FormFieldDef,
  FormFieldType,
} from '@hcc/types';
import { makeField } from './fieldMeta';

/**
 * Pure, immutable update helpers for a form schema draft. Each returns a new
 * schema so React state updates stay predictable; the builder components hold
 * the draft in useState and call these.
 */

export function emptyDraft(): FormSchema {
  return { pages: [{ title: 'Page 1', fields: [] }] };
}

function mapPage(
  schema: FormSchema,
  pageIdx: number,
  fn: (page: FormPage) => FormPage,
): FormSchema {
  return {
    ...schema,
    pages: schema.pages.map((p, i) => (i === pageIdx ? fn(p) : p)),
  };
}

function move<T>(arr: T[], from: number, dir: -1 | 1): T[] {
  const to = from + dir;
  if (to < 0 || to >= arr.length) return arr;
  const next = [...arr];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return next;
}

/* ---- pages ---- */

export function addPage(schema: FormSchema): FormSchema {
  return {
    ...schema,
    pages: [...schema.pages, { title: `Page ${schema.pages.length + 1}`, fields: [] }],
  };
}

export function removePage(schema: FormSchema, pageIdx: number): FormSchema {
  if (schema.pages.length <= 1) return schema; // always keep one page
  return { ...schema, pages: schema.pages.filter((_, i) => i !== pageIdx) };
}

export function movePage(schema: FormSchema, pageIdx: number, dir: -1 | 1): FormSchema {
  return { ...schema, pages: move(schema.pages, pageIdx, dir) };
}

export function updatePage(
  schema: FormSchema,
  pageIdx: number,
  patch: Partial<Pick<FormPage, 'title' | 'description' | 'visible_if'>>,
): FormSchema {
  return mapPage(schema, pageIdx, (p) => ({ ...p, ...patch }));
}

/* ---- fields ---- */

/** Count every field across all pages so generated keys never collide. */
export function totalFields(schema: FormSchema): number {
  return schema.pages.reduce((n, p) => n + p.fields.length, 0);
}

export function addField(
  schema: FormSchema,
  pageIdx: number,
  type: FormFieldType,
): FormSchema {
  const field = makeField(type, totalFields(schema) + 1);
  return mapPage(schema, pageIdx, (p) => ({ ...p, fields: [...p.fields, field] }));
}

export function updateField(
  schema: FormSchema,
  pageIdx: number,
  fieldIdx: number,
  field: FormFieldDef,
): FormSchema {
  return mapPage(schema, pageIdx, (p) => ({
    ...p,
    fields: p.fields.map((f, i) => (i === fieldIdx ? field : f)),
  }));
}

export function removeField(
  schema: FormSchema,
  pageIdx: number,
  fieldIdx: number,
): FormSchema {
  return mapPage(schema, pageIdx, (p) => ({
    ...p,
    fields: p.fields.filter((_, i) => i !== fieldIdx),
  }));
}

export function moveField(
  schema: FormSchema,
  pageIdx: number,
  fieldIdx: number,
  dir: -1 | 1,
): FormSchema {
  return mapPage(schema, pageIdx, (p) => ({ ...p, fields: move(p.fields, fieldIdx, dir) }));
}

/* ---- consent ---- */

/** True when any field on the form is flagged special-category. */
export function hasSpecialCategory(schema: FormSchema): boolean {
  return schema.pages.some((p) => p.fields.some((f) => f.special_category));
}

export function setConsentStatement(schema: FormSchema, statement: string): FormSchema {
  return { ...schema, consent: { required: true, statement } };
}
